import { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { TabView, TabPanel } from 'primereact/tabview';
import InputText from "../../components/InputText";
import { Checkbox } from 'primereact/checkbox';
import StateSelector from "../../components/StateSelector";
import WarningArea from "../../components/WarningArea";

const GuarantorForm = ({
    initialGuarantors = [],
    onGuarantorsChange,
    guarantorValidationSchema,
    toastRef,
}) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [guarantorForms, setGuarantorForms] = useState([]);
    const [guarantorErrors, setGuarantorErrors] = useState([]);
    const [guarantorTouched, setGuarantorTouched] = useState([]);
    const [deletedGuarantorIds, setDeletedGuarantorIds] = useState([]);
    const isInitializedRef = useRef(false);
    const shouldValidateRef = useRef(false);

    const createEmptyGuarantor = () => ({
        id: null,
        name: "",
        surname: "",
        identityNumber: "",
        phoneNumber1: "",
        phoneNumber2: "",
        countryOfAddress: null,
        districtOfAddress: null,
        detailOfAddress: "",
        countryOfWorkAddress: null,
        districtOfWorkAddress: null,
        detailOfWorkAddress: "",
        birthday: null,
        countryOfBirth: null,
        districtOfBirth: null,
        isBusinessCustomer: false,
        vkn: "",
        companyName: "",
        kvkk: 0
    });

    useEffect(() => {
        if (isInitializedRef.current) return;

        if (initialGuarantors && initialGuarantors.length > 0) {
            setGuarantorForms([...initialGuarantors]);
            setGuarantorErrors(initialGuarantors.map(() => ({})));
            setGuarantorTouched(initialGuarantors.map(() => ({})));
        } else {
            setGuarantorForms([createEmptyGuarantor()]);
            setGuarantorErrors([{}]);
            setGuarantorTouched([{}]);
        }

        isInitializedRef.current = true;
    }, [initialGuarantors]);

    useEffect(() => {
        if (isInitializedRef.current && onGuarantorsChange) {
            const guarantorsToPass = [...guarantorForms];
            const deletedIdsToPass = [...deletedGuarantorIds];

            onGuarantorsChange(guarantorsToPass, deletedIdsToPass, validateAllGuarantors);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guarantorForms, deletedGuarantorIds]);

    const validateAllGuarantors = async () => {
        shouldValidateRef.current = true;

        const guarantorsWithData = guarantorForms.filter(guarantor =>
            guarantor.name?.trim() ||
            guarantor.surname?.trim() ||
            guarantor.identityNumber?.trim() ||
            guarantor.phoneNumber1?.trim()
        );

        if (guarantorsWithData.length === 0) {
            return true;
        }

        let isValid = true;

        for (let i = 0; i < guarantorForms.length; i++) {
            const hasData = guarantorForms[i].name?.trim() ||
                guarantorForms[i].surname?.trim() ||
                guarantorForms[i].identityNumber?.trim() ||
                guarantorForms[i].phoneNumber1?.trim();

            if (!hasData) continue;

            const valid = await validateGuarantor(guarantorForms[i], i);
            if (!valid) isValid = false;

            const allTouched = {};
            Object.keys(guarantorForms[i]).forEach(key => {
                allTouched[key] = true;
            });

            const newTouched = [...guarantorTouched];
            newTouched[i] = allTouched;
            setGuarantorTouched(newTouched);
        }

        return isValid;
    };

    const validateGuarantor = async (guarantor, index) => {
        if (!guarantorValidationSchema) return true;

        try {
            await guarantorValidationSchema.validate(guarantor, { abortEarly: false });
            const newErrors = [...guarantorErrors];
            newErrors[index] = {};
            setGuarantorErrors(newErrors);
            return true;
        } catch (validationError) {
            const errors = {};
            validationError.inner.forEach(err => {
                errors[err.path] = err.message;
            });

            const newErrors = [...guarantorErrors];
            newErrors[index] = errors;
            setGuarantorErrors(newErrors);
            return false;
        }
    };

    const addNewGuarantor = () => {
        const newGuarantor = createEmptyGuarantor();
        setGuarantorForms(prev => [...prev, newGuarantor]);
        setGuarantorErrors(prev => [...prev, {}]);
        setGuarantorTouched(prev => [...prev, {}]);
        setActiveIndex(guarantorForms.length);
    };

    const removeGuarantor = (index) => {
        const guarantorToRemove = guarantorForms[index];

        if (guarantorForms.length <= 1) {
            const emptyGuarantor = createEmptyGuarantor();

            const newDeletedIds = guarantorToRemove?.id
                ? [...deletedGuarantorIds, guarantorToRemove.id]
                : [...deletedGuarantorIds];

            setDeletedGuarantorIds(newDeletedIds);
            setGuarantorForms([emptyGuarantor]);
            setGuarantorErrors([{}]);
            setGuarantorTouched([{}]);
            setActiveIndex(0);

            return;
        }

        const updatedForms = [...guarantorForms];
        updatedForms.splice(index, 1);

        if (guarantorToRemove?.id) {
            setDeletedGuarantorIds(prev => [...prev, guarantorToRemove.id]);
        }

        setGuarantorForms(updatedForms);

        setGuarantorErrors(prev => {
            const newErrors = [...prev];
            newErrors.splice(index, 1);
            return newErrors;
        });

        setGuarantorTouched(prev => {
            const newTouched = [...prev];
            newTouched.splice(index, 1);
            return newTouched;
        });

        if (activeIndex >= index && activeIndex > 0) {
            setActiveIndex(activeIndex - 1);
        }

    };

    const handleFieldChange = (index, fieldName, value) => {
        if (index >= guarantorForms.length) return;

        setGuarantorForms(prev => {
            const newForms = [...prev];
            if (!newForms[index]) return newForms;

            const updatedGuarantor = { ...newForms[index] };

            if (fieldName === "countryOfAddress" && updatedGuarantor.districtOfAddress) {
                updatedGuarantor.districtOfAddress = null;
            } else if (fieldName === "countryOfWorkAddress" && updatedGuarantor.districtOfWorkAddress) {
                updatedGuarantor.districtOfWorkAddress = null;
            } else if (fieldName === "countryOfBirth" && updatedGuarantor.districtOfBirth) {
                updatedGuarantor.districtOfBirth = null;
            }

            // Tarih alanı için özel işlem - string'i Date objesine çevir
            if (fieldName === "birthday" && value) {
                updatedGuarantor[fieldName] = typeof value === 'string' ? new Date(value) : value;
            } else {
                updatedGuarantor[fieldName] = value;
            }

            newForms[index] = updatedGuarantor;
            return newForms;
        });

        if (shouldValidateRef.current) {
            setGuarantorTouched(prev => {
                const newTouched = [...prev];
                if (!newTouched[index]) newTouched[index] = {};
                newTouched[index][fieldName] = true;
                return newTouched;
            });

            setTimeout(() => {
                if (shouldValidateRef.current && guarantorForms[index]) {
                    validateGuarantor(guarantorForms[index], index);
                }
            }, 0);
        }
    };

    const handleFieldBlur = (index, fieldName) => {
        if (index >= guarantorForms.length) return;

        setGuarantorTouched(prev => {
            const newTouched = [...prev];
            if (!newTouched[index]) newTouched[index] = {};
            newTouched[index][fieldName] = true;
            return newTouched;
        });

        if (shouldValidateRef.current && guarantorForms[index]) {
            validateGuarantor(guarantorForms[index], index);
        }
    };

    // Tarih değerini input için string formatına çevir
    const formatDateForInput = (date) => {
        if (!date) return '';
        if (typeof date === 'string') return date.split('T')[0]; // ISO string'den sadece tarih kısmını al
        if (date instanceof Date) {
            return date.toISOString().split('T')[0];
        }
        return '';
    };

    useEffect(() => {
        if (onGuarantorsChange) {
            onGuarantorsChange(guarantorForms, deletedGuarantorIds, validateAllGuarantors);
        }

        return () => {
            shouldValidateRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guarantorForms, deletedGuarantorIds]);

    return (
        <><style>
            {`
    /* Açık mod için TabView stilleri */
    .guarantor-tabview .p-tabview-nav {
        background-color: #f8f9fa;
        border-color: #dee2e6;
    }

    .guarantor-tabview .p-tabview-nav li .p-tabview-nav-link {
        color: #495057;
        background-color: #e9ecef;
        border: 1px solid #ced4da;
        border-radius: 4px;
        transition: background-color 0.3s, border-color 0.3s;
    }

    .guarantor-tabview .p-tabview-nav li .p-tabview-nav-link:hover {
        background-color: #dee2e6;
        border-color: #adb5bd;
    }

    .guarantor-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
        background-color: #00994c;
        border: 1px solid #00994c;
        color: #ffffff;
        border-radius: 4px;
        transition: background-color 0.3s, border-color 0.3s;
    }

    .guarantor-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link:hover {
        background-color: #007f3f;
        border-color: #007f3f;
    }

    .guarantor-tabview .p-tabview-panels {
        background-color: #ffffff;
        border-color: #dee2e6;
        color: #212529;
    }

    /* Koyu mod için geliştirilmiş TabView stilleri */
    .dark .guarantor-tabview .p-tabview-nav {
        background-color: #374151;
        border-color: #4b5563;
    }

    .dark .guarantor-tabview .p-tabview-nav li .p-tabview-nav-link {
        color: #e5e7eb;
        background-color: #5E6A7C;
        border: 1px solid #6b7280;
        border-radius: 4px;
        transition: background-color 0.3s, border-color 0.3s;
    }

    .dark .guarantor-tabview .p-tabview-nav li .p-tabview-nav-link:hover {
        background-color: #4a5568;
        border-color: #4b5563;
    }

    .dark .guarantor-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
        background-color: #00994c;
        border: 1px solid #00994c;
        color: #ffffff;
        border-radius: 4px;
        transition: background-color 0.3s, border-color 0.3s;
    }

    .dark .guarantor-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link:hover {
        background-color: #007f3f;
        border-color: #007f3f;
    }

    .dark .guarantor-tabview .p-tabview-panels {
        background-color: #374151;
        border-color: #4b5563;
        color: #f3f4f6;
    }

    .dark .p-button.p-button-text {
        color: #e5e7eb;
    }

    .dark .p-button.p-button-text.p-button-danger {
        color: #f87171;
    }

    .dark .p-dropdown {
        background-color: #1f2937;
        border-color: #4b5563;
    }

    .dark .p-dropdown .p-dropdown-label {
        color: #e5e7eb;
    }

    .dark .p-dropdown-panel {
        background-color: #1f2937;
        border-color: #4b5563;
    }

    .dark .p-dropdown-panel .p-dropdown-items .p-dropdown-item {
        color: #e5e7eb;
    }

    .dark .p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
        background-color: #3b82f6;
        color: #ffffff;
    }

    .dark .p-inputtext {
        color: #e5e7eb;
        background-color: #1f2937;
        border-color: #4b5563;
    }

    .dark .p-checkbox .p-checkbox-box {
        background-color: #1f2937;
        border-color: #4b5563;
    }
    `}
        </style>
            <div className="guarantor-form bg-white dark:bg-gray-900 py-2 rounded-lg shadow-md transition-all duration-300 ease-in-out w-full">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Kefil Bilgileri</h3>
                    <Button
                        type="button"
                        label="Kefil Ekle"
                        icon="pi pi-plus"
                        className="rounded-xl
                            shadow-md
                            cursor-pointer
                            border
                            bg-transparent
                            text-amber-700
                            dark:text-white
                            border-amber-700
                            hover:border-amber-500
                            hover:bg-amber-500
                            hover:text-white
                            dark:bg-amber-600 
                            dark:border-amber-600
                            dark:hover:bg-amber-700
                            dark:hover:border-amber-700
                            dark:shadow-lg
                            p-3
                            transition-all
                            duration-200"
                        onClick={addNewGuarantor}
                    />
                </div>

                {guarantorForms.length > 0 ? (
                    <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)} className="w-full guarantor-tabview">
                        {guarantorForms.map((guarantor, index) => {
                            const errors = guarantorErrors[index] || {};
                            const touched = guarantorTouched[index] || {};

                            const showErrors = shouldValidateRef.current;

                            return (
                                <TabPanel key={index} header={`Kefil ${index + 1}`} className="w-full">
                                    <div className="flex justify-between items-center mb-4">
                                        <h4 className="text-md text-gray-700 dark:text-gray-300 font-bold">{index + 1}. Kefil Paneli</h4>
                                        <Button
                                            type="button"
                                            icon="pi pi-trash"
                                            className="p-button-rounded p-button-danger p-button-text"
                                            onClick={() => removeGuarantor(index)}
                                            tooltip={guarantor.id ? "Kefil Sil" : "İptal Et"}
                                            tooltipOptions={{ position: "top" }}
                                        />
                                    </div>
                                    <div className="flex flex-col space-y-6">
                                        {/* İsim Soyisim */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-4">
                                            <InputText
                                                camelCase={true}
                                                label="İsim"
                                                name={`guarantor[${index}].name`}
                                                value={guarantor.name || ""}
                                                onChange={(e) => handleFieldChange(index, "name", e.target.value)}
                                                onBlur={() => handleFieldBlur(index, "name")}
                                                clearFunction={() => handleFieldChange(index, "name", "")}
                                                error={showErrors && touched.name ? errors.name : undefined}
                                                touched={showErrors && touched.name}
                                                required
                                            />
                                            <InputText
                                                camelCase={true}
                                                label="Soyisim"
                                                name={`guarantor[${index}].surname`}
                                                value={guarantor.surname || ""}
                                                onChange={(e) => handleFieldChange(index, "surname", e.target.value)}
                                                onBlur={() => handleFieldBlur(index, "surname")}
                                                clearFunction={() => handleFieldChange(index, "surname", "")}
                                                error={showErrors && touched.surname ? errors.surname : undefined}
                                                touched={showErrors && touched.surname}
                                                required
                                            />
                                        </div>

                                        {/* TC Kimlik No */}
                                        <InputText
                                            label="T.C Kimlik No"
                                            name={`guarantor[${index}].identityNumber`}
                                            value={guarantor.identityNumber || ""}
                                            onChange={(e) => handleFieldChange(index, "identityNumber", e.target.value)}
                                            onBlur={() => handleFieldBlur(index, "identityNumber")}
                                            clearFunction={() => handleFieldChange(index, "identityNumber", "")}
                                            error={showErrors && touched.identityNumber ? errors.identityNumber : undefined}
                                            touched={showErrors && touched.identityNumber}
                                            required
                                        />

                                        {/* Telefon Numarası */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-4">
                                            <InputText
                                                type="phone"
                                                label="Telefon Numarası"
                                                name={`guarantor[${index}].phoneNumber1`}
                                                value={guarantor.phoneNumber1 || ""}
                                                onChange={(e) => handleFieldChange(index, "phoneNumber1", e.target.value)}
                                                onBlur={() => handleFieldBlur(index, "phoneNumber1")}
                                                clearFunction={() => handleFieldChange(index, "phoneNumber1", "")}
                                                error={showErrors && touched.phoneNumber1 ? errors.phoneNumber1 : undefined}
                                                touched={showErrors && touched.phoneNumber1}
                                                required
                                            />
                                            <InputText
                                                type="phone"
                                                label="Telefon Numarası - 2"
                                                name={`guarantor[${index}].phoneNumber2`}
                                                value={guarantor.phoneNumber2 || ""}
                                                onChange={(e) => handleFieldChange(index, "phoneNumber2", e.target.value)}
                                                onBlur={() => handleFieldBlur(index, "phoneNumber2")}
                                                clearFunction={() => handleFieldChange(index, "phoneNumber2", "")}
                                                error={showErrors && touched.phoneNumber2 ? errors.phoneNumber2 : undefined}
                                                touched={showErrors && touched.phoneNumber2}
                                            />
                                        </div>

                                        {/* Doğum Yeri (StateSelector) */}
                                        <StateSelector
                                            formik={{
                                                values: guarantor,
                                                errors: showErrors ? errors : {},
                                                touched: showErrors ? touched : {},
                                                handleBlur: (e) => {
                                                    if (e.target && e.target.name) {
                                                        const field = e.target.name.replace(`guarantor[${index}].`, '');
                                                        handleFieldBlur(index, field);
                                                    }
                                                },
                                                setFieldValue: (field, value) => handleFieldChange(index, field, value),
                                                setFieldTouched: (field) => handleFieldBlur(index, field)
                                            }}
                                            labelIl="Doğum Yeri İl"
                                            labelIlce="Doğum Yeri İlçe"
                                            ilFieldName="countryOfBirth"
                                            ilceFieldName="districtOfBirth"
                                            required
                                        />

                                        {/* Doğum Tarihi */}
                                        <InputText
                                            type="date"
                                            label="Doğum Tarihi"
                                            name={`guarantor[${index}].birthday`}
                                            value={formatDateForInput(guarantor.birthday)}
                                            onChange={(e) => handleFieldChange(index, "birthday", e.target.value)}
                                            onBlur={() => handleFieldBlur(index, "birthday")}
                                            clearFunction={() => handleFieldChange(index, "birthday", null)}
                                            error={showErrors && touched.birthday ? errors.birthday : undefined}
                                            touched={showErrors && touched.birthday}
                                            required
                                        />

                                        {/* Adres İl/İlçe (StateSelector) */}
                                        <StateSelector
                                            formik={{
                                                values: guarantor,
                                                errors: showErrors ? errors : {},
                                                touched: showErrors ? touched : {},
                                                handleBlur: (e) => {
                                                    if (e.target && e.target.name) {
                                                        const field = e.target.name.replace(`guarantor[${index}].`, '');
                                                        handleFieldBlur(index, field);
                                                    }
                                                },
                                                setFieldValue: (field, value) => handleFieldChange(index, field, value),
                                                setFieldTouched: (field) => handleFieldBlur(index, field)
                                            }}
                                            labelIl="İkametgah İl"
                                            labelIlce="İkametgah İlçe"
                                            ilFieldName="countryOfAddress"
                                            ilceFieldName="districtOfAddress"
                                            required
                                        />

                                        {/* Adres */}
                                        <WarningArea type='info' message="Lütfen adres detay bilgilerinizi örnekte belirtildiği şekilde eksiksiz ve doğru olarak giriniz. Senet çıktılarınızdada il ve ilçe bilgisi, ilgili seçim alanlarından otomatik olarak alınmaktadır. Örnek adres formatı: Murtaza Mahallesi Hacı Bülbül Sokak Örnek Apartmanı No:13/5" />
                                        <InputText
                                            type="textarea"
                                            label="İkamet Adres Detayı"
                                            name={`guarantor[${index}].detailOfAddress`}
                                            value={guarantor.detailOfAddress || ""}
                                            onChange={(e) => handleFieldChange(index, "detailOfAddress", e.target.value)}
                                            onBlur={() => handleFieldBlur(index, "detailOfAddress")}
                                            clearFunction={() => handleFieldChange(index, "detailOfAddress", "")}
                                            error={showErrors && touched.detailOfAddress ? errors.detailOfAddress : undefined}
                                            touched={showErrors && touched.detailOfAddress}
                                            required
                                        />

                                        {/* Ticari Kefil CheckBox */}
                                        <div className="flex items-center justify-center my-3">
                                            <Checkbox
                                                inputId={`isBusinessCustomer-${index}`}
                                                className="shadow-lg rounded-lg border-2"
                                                checked={guarantor.isBusinessCustomer || false}
                                                onChange={(e) => {
                                                    handleFieldChange(index, "isBusinessCustomer", e.checked);

                                                    if (!e.checked) {
                                                        handleFieldChange(index, "vkn", "");
                                                        handleFieldChange(index, "companyName", "");
                                                        handleFieldChange(index, "countryOfWorkAddress", null);
                                                        handleFieldChange(index, "districtOfWorkAddress", null);
                                                        handleFieldChange(index, "detailOfWorkAddress", "");
                                                    }
                                                }}
                                            />
                                            <label htmlFor={`isBusinessCustomer-${index}`} className="ml-2 text-gray-700 dark:text-gray-300">Ticari kefil mi?</label>
                                        </div>

                                        {/* Ticari Kefil Bilgileri */}
                                        {guarantor.isBusinessCustomer && (
                                            <div className="space-y-4">
                                                <InputText
                                                    label="VKN"
                                                    name={`guarantor[${index}].vkn`}
                                                    value={guarantor.vkn || ""}
                                                    onChange={(e) => handleFieldChange(index, "vkn", e.target.value)}
                                                    onBlur={() => handleFieldBlur(index, "vkn")}
                                                    clearFunction={() => handleFieldChange(index, "vkn", "")}
                                                    error={showErrors && touched.vkn ? errors.vkn : undefined}
                                                    touched={showErrors && touched.vkn}
                                                    required
                                                />
                                                <InputText
                                                    camelCase={true}
                                                    label="Firma Ünvanı"
                                                    name={`guarantor[${index}].companyName`}
                                                    value={guarantor.companyName || ""}
                                                    onChange={(e) => handleFieldChange(index, "companyName", e.target.value)}
                                                    onBlur={() => handleFieldBlur(index, "companyName")}
                                                    clearFunction={() => handleFieldChange(index, "companyName", "")}
                                                    error={showErrors && touched.companyName ? errors.companyName : undefined}
                                                    touched={showErrors && touched.companyName}
                                                    required
                                                />

                                                {/* İş Yeri (StateSelector) */}
                                                <StateSelector
                                                    formik={{
                                                        values: guarantor,
                                                        errors: showErrors ? errors : {},
                                                        touched: showErrors ? touched : {},
                                                        handleBlur: (e) => {
                                                            if (e.target && e.target.name) {
                                                                const field = e.target.name.replace(`guarantor[${index}].`, '');
                                                                handleFieldBlur(index, field);
                                                            }
                                                        },
                                                        setFieldValue: (field, value) => handleFieldChange(index, field, value),
                                                        setFieldTouched: (field) => handleFieldBlur(index, field)
                                                    }}
                                                    labelIl="İş Yeri İl"
                                                    labelIlce="İş Yeri İlçe"
                                                    ilFieldName="countryOfWorkAddress"
                                                    ilceFieldName="districtOfWorkAddress"
                                                    required={guarantor.isBusinessCustomer}
                                                />

                                                {/* İş Adresi */}
                                                <WarningArea type='info' message="Lütfen adres detay bilgilerinizi örnekte belirtildiği şekilde eksiksiz ve doğru olarak giriniz. Senet çıktılarınızdada il ve ilçe bilgisi, ilgili seçim alanlarından otomatik olarak alınmaktadır. Örnek adres formatı: Murtaza Mahallesi Hacı Bülbül Sokak Örnek Apartmanı No:13/5" />
                                                <InputText
                                                    camelCase={true}
                                                    type="textarea"
                                                    label="İş Yeri Adres Detayı"
                                                    name={`guarantor[${index}].detailOfWorkAddress`}
                                                    value={guarantor.detailOfWorkAddress || ""}
                                                    onChange={(e) => handleFieldChange(index, "detailOfWorkAddress", e.target.value)}
                                                    onBlur={() => handleFieldBlur(index, "detailOfWorkAddress")}
                                                    clearFunction={() => handleFieldChange(index, "detailOfWorkAddress", "")}
                                                    error={showErrors && touched.detailOfWorkAddress ? errors.detailOfWorkAddress : undefined}
                                                    touched={showErrors && touched.detailOfWorkAddress}
                                                    required
                                                />
                                            </div>
                                        )}
                                    </div>
                                </TabPanel>
                            );
                        })}
                    </TabView>
                ) : (
                    <div className="flex justify-center p-6">
                        <Button
                            type="button"
                            label="Kefil Ekle"
                            icon="pi pi-plus"
                            className="rounded-xl shadow-lg cursor-pointer text-white bg-green-600 hover:bg-green-700 p-2"
                            onClick={addNewGuarantor}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default GuarantorForm;